import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  CalendarClock,
  ExternalLink,
  Eye,
  EyeOff,
  Globe,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Wallet as WalletIcon,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useProfile } from '../hooks/useProfile'
import { usePageTitle } from '../hooks/usePageTitle'
import { LEGACY_PRODUCT_NAME } from '../lib/product'
import { useWallet } from '../hooks/useWallet'
import { Avatar, Badge, Button, Card, Dialog, Skeleton, SkeletonGroup, Spinner, Toggle, useToast } from '../components/ui'
import { formatBalance } from '../components/wallet/money'
import { formatDate, librarySyncState } from '../lib/format'

type ConsentField = 'recommendation_consent' | 'aggregate_consent'

/**
 * The logged-in user's Steam identity (read-only) plus the editable account
 * surface: consent toggles, a force-fresh "Re-sync from Steam", and a GDPR
 * "Delete my data" request. Renders only what /api/v1/me returns.
 */
export function ProfileScreen() {
  usePageTitle('Your profile', LEGACY_PRODUCT_NAME)
  const { user, isLoading, features } = useAuth()
  const { updateConsent, resyncSteam, deleteAccount, awaitLibrarySync } = useProfile()
  // §3.7 wallet teaser — the hook skips fetching entirely when the flag is off.
  const { wallet } = useWallet({ enabled: features.group_wallet })
  const { show } = useToast()
  const navigate = useNavigate()

  const [syncing, setSyncing] = useState(false)
  const [savingConsent, setSavingConsent] = useState(false)
  const [syncingLibrary, setSyncingLibrary] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <Skeleton className="h-8 w-40" />
        <SkeletonGroup label="Loading your profile" className="space-y-6">
          <div className="space-y-4 rounded-card bg-surface p-5">
            <div className="flex items-start gap-4">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-64" />
              </div>
            </div>
          </div>
          <Skeleton className="h-24 w-full rounded-card" />
          <Skeleton className="h-32 w-full rounded-card" />
        </SkeletonGroup>
      </div>
    )
  }
  if (!user) {
    return <p className="mx-auto max-w-3xl px-4 py-8 text-text-muted">You&rsquo;re not signed in.</p>
  }

  const visibility = user.profile_visibility ?? 'unknown'
  const VisibilityIcon = visibility === 'public' ? Eye : EyeOff
  // Honest library-sync read (§5.2): a completed-but-empty sync must say so —
  // never "Not synced yet" while pointing at a consent toggle that's already on.
  const libraryState = librarySyncState(user)

  async function saveConsent(field: ConsentField, value: boolean) {
    setSavingConsent(true)
    setError(null)
    try {
      await updateConsent({ [field]: value })
      show('Preferences saved', { tone: 'success' })
      if (field === 'recommendation_consent' && value) {
        setSyncingLibrary(true)
        await awaitLibrarySync()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your preferences.')
    } finally {
      setSavingConsent(false)
      setSyncingLibrary(false)
    }
  }

  async function handleResync() {
    setSyncing(true)
    setError(null)
    try {
      await resyncSteam()
      show('Profile up to date', { tone: 'success' })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not re-sync from Steam.')
    } finally {
      setSyncing(false)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    setError(null)
    try {
      await deleteAccount()
      await navigate({ to: '/steam' })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete your data.')
      setDeleting(false)
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="font-display text-2xl text-text-strong">Your profile</h1>

      {error && (
        <p role="alert" className="text-sm text-danger-fg">
          {error}
        </p>
      )}

      <Card elevation="raised" className="space-y-4">
        <div className="flex items-start gap-4">
          <Avatar name={user.display_name} src={user.avatar_url} size="lg" className="h-20 w-20 text-2xl" />
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-2xl text-text-strong">{user.display_name ?? 'You'}</h2>
            {user.real_name && <p className="text-sm text-text-muted">{user.real_name}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {user.country_code && (
                <Badge icon={<Globe className="h-3.5 w-3.5" />}>{user.country_code}</Badge>
              )}
              {user.member_since && (
                <Badge icon={<CalendarClock className="h-3.5 w-3.5" />}>
                  Since {formatDate(user.member_since)}
                </Badge>
              )}
              <Badge icon={<VisibilityIcon className="h-3.5 w-3.5" />}>{visibility}</Badge>
              {user.steam_id && <Badge className="font-mono">{user.steam_id}</Badge>}
            </div>
            {user.profile_url && (
              <a
                href={user.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 rounded-control text-sm text-ember-300 hover:text-ember-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              >
                View Steam profile <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only"> (opens in new tab)</span>
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          {/* muted: sync status is real content, faint fails 4.5:1 (WCAG 1.4.3). */}
          <p className="text-xs text-text-muted">
            {user.profile_synced_at ? `Last synced ${formatDate(user.profile_synced_at)}` : 'Not synced yet'}
          </p>
          <Button variant="secondary" size="sm" loading={syncing} onClick={() => void handleResync()}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" /> Refresh profile
          </Button>
        </div>
      </Card>

      <Card className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium text-text">Steam library</h2>
            <p className="flex items-center gap-1.5 text-xs text-text-muted">
              {libraryState === 'synced' ? (
                `${user.owned_game_count} ${user.owned_game_count === 1 ? 'game' : 'games'}${
                  user.last_synced_at ? ` · synced ${formatDate(user.last_synced_at)}` : ''
                }`
              ) : libraryState === 'synced_empty' ? (
                `Synced${user.last_synced_at ? ` ${formatDate(user.last_synced_at)}` : ''} — no games on this account`
              ) : syncingLibrary ? (
                <>
                  <Spinner size="sm" aria-hidden="true" /> Syncing your Steam library…
                </>
              ) : (
                'Not synced yet'
              )}
            </p>
          </div>
          <Badge
            variant={user.library_readable ? 'success' : 'neutral'}
            icon={<ShieldCheck className="h-3.5 w-3.5" />}
          >
            {user.library_readable ? 'Readable' : 'Not readable'}
          </Badge>
        </div>
        {libraryState === 'synced' && (
          <Link
            to="/steam/library"
            className="inline-flex items-center gap-1 text-xs text-ember-300 transition hover:text-ember-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            View your library →
          </Link>
        )}
        {libraryState === 'never_synced' && !syncingLibrary && (
          <p className="text-xs text-text-muted">
            Turn on <span className="text-text">Personalized recommendations</span> below to sync
            your Steam library.
          </p>
        )}
        {libraryState === 'syncing' && !syncingLibrary && (
          <p className="text-xs text-text-muted">
            We&rsquo;re waiting on your first sync. If your Steam game details are set to private, we
            can&rsquo;t read your library.
          </p>
        )}
      </Card>

      {features.group_wallet && (
        <Card className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-medium text-text">Wallet</h2>
              <p className="text-xs text-text-muted">
                {wallet
                  ? `${formatBalance(wallet.balance_cents, wallet.currency)} available`
                  : 'Top up to chip in on group gifts.'}
              </p>
            </div>
            <WalletIcon className="h-5 w-5 text-text-muted" strokeWidth={1.75} aria-hidden="true" />
          </div>
          <Link
            to="/steam/wallet"
            className="inline-flex items-center gap-1 text-xs text-ember-300 transition hover:text-ember-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            Manage wallet →
          </Link>
        </Card>
      )}

      <Card elevation="raised" className="space-y-4">
        <h2 className="font-display text-lg text-text-strong">Preferences</h2>
        <Toggle
          label="Personalized recommendations"
          description="Opt in to sync your Steam library and get game-night picks for your group. Turning this off deletes your synced library."
          checked={user.recommendation_consent}
          disabled={savingConsent}
          onChange={(v) => void saveConsent('recommendation_consent', v)}
        />
      </Card>

      <Card className="space-y-3 border-danger/30">
        <h2 className="font-display text-lg text-text-strong">Delete my data</h2>
        <p className="text-sm text-text-muted">
          Revoke all consent, mark your account for deletion, and sign out. Your Steam library and
          playtime stop being used immediately.
        </p>
        <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
          <Trash2 className="h-4 w-4" aria-hidden="true" /> Delete my data
        </Button>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Delete your data?">
        <p className="text-sm text-text-muted">
          This revokes your consent, marks your account for deletion, and signs you out. It can&rsquo;t
          be undone here.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="destructive" loading={deleting} onClick={() => void handleDelete()}>
            Yes, delete my data
          </Button>
        </div>
      </Dialog>
    </div>
  )
}
